import { IconPlus, IconPlusCircle } from "@/assets/icons";
import { BillingRecipientRow } from "@/features/company/components/sections/BillingRecipientRow";
import { BILLING_RECIPIENT_COLUMNS, BILLING_RECIPIENT_GRID_TEMPLATE } from "@/features/company/constants/billing-recipient-columns";
import { BILLING_RECIPIENT_EMPTY_TEXT } from "@/features/company/constants/sections";
import { TCompanyForm, emptyBillingRecipientRow } from "@/features/company/types/company-form";
import { ButtonNew } from "@/modules/irohana-components/elements/ButtonNew";
import { background, button, divider, text } from "@/theme/colors";
import { Box, Stack, Typography, styled } from "@mui/material";
import { FC } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";

/** 請求先情報 section: an editable table of 拠点 that receive invoices, one row each. */
export const BillingRecipientSection: FC = () => {
  const { control } = useFormContext<TCompanyForm>();
  const { fields, append, remove } = useFieldArray({ control, name: "billingRecipients" });

  const handleAddRow = () => append(emptyBillingRecipientRow());

  if (!fields.length) {
    return (
      <Stack alignItems="center" gap="16px" py="16px">
        <Typography variant="body14" color={text.secondary}>
          {BILLING_RECIPIENT_EMPTY_TEXT}
        </Typography>
        <ButtonNew variant="secondary" size="sm" startIcon={<IconPlus />} onClick={handleAddRow}>
          新規請求先作成
        </ButtonNew>
      </Stack>
    );
  }

  return (
    <Stack gap="8px">
      <TableScroll>
        <Box minWidth="fit-content">
          <HeaderRow gridTemplateColumns={BILLING_RECIPIENT_GRID_TEMPLATE}>
            {BILLING_RECIPIENT_COLUMNS.map(({ key, label, required }) => (
              <Typography key={key} variant="cap12Semi" color={text.primary}>
                {label}
                {required && <RequiredMark>*</RequiredMark>}
              </Typography>
            ))}
            <Box />
          </HeaderRow>

          {fields.map((field, index) => (
            <BillingRecipientRow key={field.id} index={index} onRemove={() => remove(index)} />
          ))}
        </Box>
      </TableScroll>

      <Box>
        <AddRowButton variant="text" size="sm" startIcon={<IconPlusCircle color={button.primary} />} onClick={handleAddRow}>
          行を追加
        </AddRowButton>
      </Box>
    </Stack>
  );
};

/** 行を追加 — 枠線なしのテキストボタン。プラスアイコンだけをテール色にする。 */
const AddRowButton = styled(ButtonNew)`
  &.MuiButtonBase-root {
    padding-left: 0;
    color: ${text.primary};
    font-size: 12px;
    font-weight: 400;
    :hover {
      background: transparent;
      border-color: transparent;
      color: ${text.primary};
    }
  }
`;

const TableScroll = styled(Box)`
  overflow-x: auto;
  border: 1px solid ${divider.middle};
  border-radius: 8px;
`;

const HeaderRow = styled(Box)`
  display: grid;
  gap: 8px;
  align-items: center;
  padding: 8px 16px;
  background: ${background.primary};
  border-bottom: 1px solid ${divider.middle};
`;

const RequiredMark = styled("span")`
  color: ${text.error};
  margin-left: 2px;
`;
